import { ConfigConstant } from '@shared/constants/config.constant';
import { FileConstants } from '@shared/constants/file.constant';
import { Country } from '@shared-features/models';

type duration = {
  days: string;
  hours: string;
  mins: string;
};
export class FunctionUtil {
  static deepClone(obj) {
    if (!obj || true === obj) {
      // this also handles boolean as true and false
      return obj;
    }
    const objType = typeof obj;
    if ('number' === objType || 'string' === objType || 'object' === objType) {
      // add your immutables here
      return obj;
    }
    const result = Array.isArray(obj) ? [] : !obj.constructor ? {} : new obj.constructor();
    if (obj instanceof Map) {
      for (const key of obj.keys()) {
        result.set(key, this.deepClone(obj.get(key)));
      }
    }
    for (const key2 in obj) {
      if (obj?.hasOwnProperty(key2)) {
        result[key2] = this.deepClone(obj[key2]);
      }
    }
    return result;
  }

  /**
   * generate groups of 4 random characters
   * @example getUniqueId(1) : 607f
   * @example getUniqueId(4) : 95ca-361a-f8a1-1e73
   */
  static getUniqueId(parts: number): string {
    const stringArr = [];
    for (let i = 0; i < parts; i++) {
      // tslint:disable-next-line:no-bitwise
      const S4 = (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
      stringArr.push(S4);
    }
    return stringArr.join('-');
  }

  static formatFileSize(size) {
    for (let i = 1; i < FileConstants.fileSizes.length; i++) {
      if (size < Math.pow(1024, i)) {
        return Math.round((size / Math.pow(1024, i - 1)) * 100) / 100 + FileConstants.fileSizes[i - 1];
      }
    }
    return size;
  }

  static convertDate(value) {
    let date = new Date(value),
      month = ('0' + (date.getMonth() + 1)).slice(-2),
      day = ('0' + date.getDate()).slice(-2);
    return [date.getFullYear(), month, day].join('-');
  }

  static convertDateWithoutDays(value): string {
    const date = new Date(value);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    return [date.getFullYear(), month].join('-');
  }

  static formateCallDurationByFractions(duration): duration {
    if (isNaN(duration)) {
      return {
        days: '00',
        hours: '00',
        mins: '00',
      };
    }
    let num = Number(duration) / 1000;
    let days = num / (24 * 60 * 60);
    if (days < 1) {
      days = 0;
    }
    let hour = num / 3600;
    hour = hour - Math.floor(days) * 24;
    let fractionhour = hour - Math.floor(hour);
    if (hour < 1.0) {
      fractionhour = hour;
      hour = 0;
    }
    let min = fractionhour * 60;
    let fractionmin = min - Math.floor(min);
    if (min < 1.0) {
      fractionmin = min;
      min = 0;
    }

    return {
      days: Math.floor(days).toString(),
      hours: Math.floor(hour).toString(),
      mins: Math.floor(min).toString(),
    };
  }

  static getSaudiArabiaPhoneKey(countries: Country[]): string {
    let _country = new Country();
    countries.some((country) => {
      if (country.isoCode === ConfigConstant.SAUDI_ARABIA_ISO_CODE) {
        _country = country;
        return true;
      }
    });
    return _country.phoneKey;
  }

  static arrayEquals(a, b): boolean {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((val, index) => val === b[index]);
  }

  static getObjectDiff<T extends object>(
    original: T,
    updated: T,
    requiredKeys: (keyof T)[] = [], // always kept
    nullableKeys: (keyof T)[] = [], // kept when updated value = '' || null || undefiend
    checkById = false,
    idKey = 'id'
  ): Partial<T> {
    const diff: Partial<T> = {};

    if (!original) {
      return updated;
    }

    const isDate = (val: unknown): val is Date => Object.prototype.toString.call(val) === '[object Date]';

    Object.keys(updated).forEach((key) => {
      const typedKey = key as keyof T;
      const newVal = updated[typedKey];
      const oldVal = original[typedKey];

      if (!original) {
        return updated;
      }
      
      if (requiredKeys.includes(typedKey) || Array.isArray(newVal)) {
        diff[typedKey] = newVal;
        return;
      }

      // special case: somethingId ↔ something.id
      if (checkById && key.endsWith('Id')) {
        const baseKey = key.slice(0, -2); // remove "Id"

        const origObj = (original as T)[baseKey];

        if (origObj && typeof origObj === 'object' && idKey in origObj) {
          // compare single object id
          if (origObj[idKey] === newVal) {
            return; // no change
          }
        }
      }

      // handle Date comparison
      if (isDate(newVal) && isDate(oldVal)) {
        if (newVal.getTime() !== oldVal.getTime()) {
          diff[typedKey] = newVal;
        }
        return;
      }

      // handle nested object
      if (
        typeof newVal === 'object' &&
        newVal !== null &&
        typeof oldVal === 'object' &&
        oldVal !== null
      ) {
        const nestedDiff = this.getObjectDiff(
          oldVal as T,
          newVal as T,
          requiredKeys,
          nullableKeys,
          checkById,
          idKey,
        );
        if (Object.keys(nestedDiff).length > 0) {
          diff[typedKey] = nestedDiff as T[keyof T];
        }
      }
      //  primitive comparison
      else if (!Object.is(oldVal, newVal)) {
        // filter empty values for none nullable keys
        if (!nullableKeys.includes(typedKey) && (newVal === null || newVal === undefined || newVal === '')) {
          return;
        }
        diff[typedKey] = newVal;
      }
    });

    requiredKeys.forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(updated, key)) {
        diff[key] = original[key];
      }
    });

    return diff;
  }

  static mergeIgnoringNulls<T>(...objects: Partial<T>[]): T {
    return objects.reduce<any>(
      (result, obj) => FunctionUtil.deepMergeIgnoringNulls(result, obj),
      {}
    ) as T;
  }

  /**
   * Merges `source` into `target` in place, ignoring null/undefined values.
   * Nested objects (e.g. `fleet`) are merged recursively so inner keys coming
   * from different responses are combined instead of the whole object being
   * replaced. Arrays, Dates and primitives keep "last non-null wins" semantics.
   */
  private static deepMergeIgnoringNulls(target: any, source: any): any {
    if (source === null || source === undefined) {
      return target;
    }

    for (const [key, value] of Object.entries(source)) {
      // Preserve an already-set, non-empty value when an empty array arrives.
      if (
        Array.isArray(value) &&
        value.length === 0 &&
        target[key] !== null &&
        target[key] !== undefined &&
        key in target
      ) {
        continue;
      }

      if (value === null || value === undefined) {
        // Only seed the key the first time; never overwrite a real value with null.
        if (!(key in target)) {
          target[key] = value;
        }
        continue;
      }

      // Recurse into nested objects so inner keys are merged, not replaced.
      if (
        FunctionUtil.isMergeableObject(value) &&
        FunctionUtil.isMergeableObject(target[key])
      ) {
        FunctionUtil.deepMergeIgnoringNulls(target[key], value);
      } else {
        target[key] = value;
      }
    }

    return target;
  }

  private static isMergeableObject(value: any): boolean {
    return (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      !(value instanceof Date)
    );
  }
}
